import { useSyncExternalStore } from 'react';

/**
 * Whether this instance has the popular endpoint switched off — the one fact the Popular menu item
 * cannot be honest without: a nav item whose page always answers "the administrator turned
 * this off" is §8's "shown broken", so the shell hides it and the settings page disables its toggle,
 * with the reason.
 *
 * The fact is *learned*, not probed: `popular_enabled: false` only shows itself when the feed is
 * fetched (`403 {"error":"Administrator has disabled this endpoint."}` → core's `endpoint-disabled`),
 * so `useFeed` records the outcome of every popular fetch here — a refusal sets the flag, a success
 * clears it. `/feed/popular` itself stays reachable by URL, which is also what lets a wrong flag
 * heal: the page's own fetch corrects it.
 *
 * Cleared on an instance switch (`useSettingsScreen.switchInstance`), because the flag is a fact
 * about the instance while this key — like every setting — is per page origin.
 */

const KEY = 'visious.popularDisabled';

const listeners = new Set<() => void>();

export function isPopularDisabled(): boolean {
  try {
    return globalThis.localStorage.getItem(KEY) === 'true';
  } catch {
    // Blocked storage means "we cannot tell", and an item must not disappear on a guess.
    return false;
  }
}

export function writePopularDisabled(disabled: boolean): void {
  try {
    if (disabled) globalThis.localStorage.setItem(KEY, 'true');
    else globalThis.localStorage.removeItem(KEY);
  } catch {
    // Nothing to recover: the flag still applies for this page load via the snapshot below.
  }
  snapshot = disabled;
  for (const listener of listeners) listener();
}

export function clearPopularDisabled(): void {
  writePopularDisabled(false);
}

let snapshot: boolean | null = null;

function getSnapshot(): boolean {
  snapshot ??= isPopularDisabled();
  return snapshot;
}

/**
 * One `storage` listener for the whole document, installed on the first subscriber and never taken
 * down. It used to be one per *subscriber*, which made a single cross-tab write cost one full re-read
 * and one round of notifications **per subscriber** — see `platform/settings.ts`, where a grid made
 * that quadratic and froze the tab for seconds.
 */
let watchingOtherTabs = false;

function watchOtherTabs(): void {
  if (watchingOtherTabs) return;
  watchingOtherTabs = true;
  // Another tab learning the fact counts — the same cross-tab reasoning `platform/settings.ts` gives.
  globalThis.addEventListener('storage', (event: StorageEvent) => {
    if (event.key === null || event.key === KEY) {
      snapshot = isPopularDisabled();
      // Every listener, not the one this handler was created beside: with a single listener for the
      // document there is no "beside" any more, and notifying one subscriber would leave the rest of
      // the page on the old answer.
      for (const listener of listeners) listener();
    }
  });
}

function subscribe(onChange: () => void): () => void {
  watchOtherTabs();
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

/** The flag, re-read whenever a fetch or another tab updates it. */
export function usePopularDisabled(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
